(() => {
  const tagName = 'goodr-vcb-carousel';

  class GoodrVcbCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.viewport = this.querySelector('[data-goodr-vcb-viewport]');
      this.track = this.querySelector('[data-goodr-vcb-track]');
      this.prevButton = this.querySelector('[data-goodr-vcb-prev]');
      this.nextButton = this.querySelector('[data-goodr-vcb-next]');
      this.dots = [...this.querySelectorAll('[data-goodr-vcb-dot]')];
      this.mainSlides = [...this.querySelectorAll('[data-goodr-vcb-copy="main"]')];
      this.allSlides = [...this.querySelectorAll('[data-goodr-vcb-slide]')];
      this.count = this.mainSlides.length;

      if (!this.viewport || !this.track || this.count === 0) return;

      this.initialized = true;
      this.currentIndex = 0;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.scrollTimer = 0;
      this.drag = null;

      this.prevButton?.addEventListener('click', () => this.step(-1), { signal: this.signal });
      this.nextButton?.addEventListener('click', () => this.step(1), { signal: this.signal });

      this.dots.forEach((dot) => {
        dot.addEventListener(
          'click',
          () => this.goTo(Number.parseInt(dot.dataset.goodrVcbDot, 10), true),
          { signal: this.signal }
        );
      });

      this.viewport.addEventListener('scroll', () => this.onScroll(), {
        passive: true,
        signal: this.signal,
      });
      this.viewport.addEventListener('keydown', (event) => this.onKeydown(event), {
        signal: this.signal,
      });

      if (this.dataset.dragDesktop !== 'false') {
        this.viewport.addEventListener('pointerdown', (event) => this.onPointerDown(event), {
          signal: this.signal,
        });
        this.viewport.addEventListener('pointermove', (event) => this.onPointerMove(event), {
          signal: this.signal,
        });
        this.viewport.addEventListener('pointerup', (event) => this.onPointerUp(event), {
          signal: this.signal,
        });
        this.viewport.addEventListener('pointercancel', (event) => this.onPointerUp(event), {
          signal: this.signal,
        });
      }

      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.viewport);

      requestAnimationFrame(() => {
        this.measure();
        this.goTo(0, false);
      });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      window.clearTimeout(this.scrollTimer);
      this.initialized = false;
    }

    measure() {
      if (!this.viewport || this.count === 0) return;

      const styles = getComputedStyle(this.viewport);
      const inlinePadding = Number.parseFloat(styles.paddingLeft) + Number.parseFloat(styles.paddingRight);
      const width = this.viewport.clientWidth;
      const slidesPerView = width < 480 ? 1 : width < 1280 ? 2 : 3;
      this.slideWidth = (width - inlinePadding) / slidesPerView;
      this.style.setProperty('--goodr-vcb-slide-width', `${this.slideWidth}px`);

      requestAnimationFrame(() => this.goTo(this.currentIndex, false));
    }

    step(delta) {
      if (!this.slideWidth) return;
      this.viewport.scrollBy({ left: delta * this.slideWidth, behavior: this.motionBehavior() });
    }

    goTo(index, smooth) {
      if (!this.slideWidth || this.count === 0) return;
      const normalized = ((index % this.count) + this.count) % this.count;
      const targetOrdinal = this.count + normalized;
      this.viewport.scrollTo({
        left: targetOrdinal * this.slideWidth,
        behavior: smooth ? this.motionBehavior() : 'auto',
      });
      this.updateCurrent(normalized);
    }

    motionBehavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    onScroll() {
      if (!this.slideWidth) return;
      const ordinal = Math.round(this.viewport.scrollLeft / this.slideWidth);
      this.updateCurrent(((ordinal % this.count) + this.count) % this.count);

      window.clearTimeout(this.scrollTimer);
      this.scrollTimer = window.setTimeout(() => this.settleLoop(), 120);
    }

    settleLoop() {
      if (!this.slideWidth || this.drag) return;
      const ordinal = Math.round(this.viewport.scrollLeft / this.slideWidth);
      let corrected = ordinal;

      if (ordinal < this.count) corrected += this.count;
      if (ordinal >= this.count * 2) corrected -= this.count;

      if (corrected !== ordinal) {
        this.viewport.scrollTo({ left: corrected * this.slideWidth, behavior: 'auto' });
      } else {
        this.viewport.scrollTo({ left: ordinal * this.slideWidth, behavior: this.motionBehavior() });
      }

      this.updateFocusableSlides();
    }

    updateCurrent(index) {
      if (index === this.currentIndex && this.dots[index]?.getAttribute('aria-current') === 'true') return;
      this.currentIndex = index;
      this.dots.forEach((dot, dotIndex) => {
        if (dotIndex === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      this.updateFocusableSlides();
    }

    updateFocusableSlides() {
      const viewportRect = this.viewport.getBoundingClientRect();

      this.mainSlides.forEach((slide) => {
        const rect = slide.getBoundingClientRect();
        const fullyVisible = rect.left >= viewportRect.left + 4 && rect.right <= viewportRect.right - 4;
        slide.querySelectorAll('a, button, input, textarea, select').forEach((control) => {
          if (fullyVisible) control.removeAttribute('tabindex');
          else control.setAttribute('tabindex', '-1');
        });
      });
    }

    onKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.step(event.key === 'ArrowLeft' ? -1 : 1);
    }

    onPointerDown(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      this.drag = { id: event.pointerId, x: event.clientX, scrollLeft: this.viewport.scrollLeft };
      this.viewport.classList.add('is-dragging');
      this.viewport.setPointerCapture(event.pointerId);
    }

    onPointerMove(event) {
      if (!this.drag || event.pointerId !== this.drag.id) return;
      this.viewport.scrollLeft = this.drag.scrollLeft + this.drag.x - event.clientX;
    }

    onPointerUp(event) {
      if (!this.drag || event.pointerId !== this.drag.id) return;
      this.viewport.releasePointerCapture?.(event.pointerId);
      this.viewport.classList.remove('is-dragging');
      this.drag = null;
      this.settleLoop();
    }
  }

  if (!customElements.get(tagName)) customElements.define(tagName, GoodrVcbCarousel);

  document.addEventListener('shopify:block:select', (event) => {
    const section = event.target.closest(tagName);
    if (!section?.initialized) return;
    const slide = section.querySelector(
      `[data-goodr-vcb-copy="main"][data-block-id="${CSS.escape(event.detail.blockId)}"]`
    );
    if (slide) section.goTo(Number.parseInt(slide.dataset.realIndex, 10), true);
  });
})();

